import { useState } from 'react';
import { TabPanel, Tabs } from '@/ui/Tabs';
import { Bandeja } from './Bandeja';
import { Configurador } from './Configurador';
import { Plantillas } from './Plantillas';

/**
 * Documentos — el back-office de Formatos PDF. `#documentos`, permiso
 * `documentos:manage`. Módulo 16.
 *
 * ## Este módulo está en retirada, y se migra igual
 *
 * Lo sustituye `solicitudes.py` desde el 2026-08-27: el portal tenía dos
 * módulos llamados «Solicitudes» a la vez, con dos series de radicado. Lo que
 * queda vivo aquí es la administración de plantillas y el acceso a lo ya
 * radicado, **que es evidencia**.
 *
 * Se rediseña con el mismo criterio que los quince anteriores porque la regla
 * del proyecto no depende de la esperanza de vida de la pantalla: mientras esté
 * alcanzable, lo que está mal no se queda. Y lo que estaba mal aquí incluía
 * nueve interpolaciones sin escapar —dos cruzando un límite de privilegio— y un
 * editor visual que **nunca llegó a cargar el documento** (ver
 * `Configurador.tsx`).
 *
 * ## Las direcciones
 *
 * | Hash | Qué abre |
 * |---|---|
 * | `#documentos` | la bandeja de radicados |
 * | `#documentos/plantillas` | los formatos publicados |
 * | `#documentos/plantilla/<id>` | dónde va cada dato sobre el PDF |
 *
 * Las dos primeras son pestañas y cambian con `replaceState`, sin remontar: el
 * router del shell vuelve a montar la capa React en cada `hashchange`, y con
 * ella nace un cliente de caché nuevo. La tercera sí es un montaje, porque es
 * otra pantalla.
 */
const PESTANAS = [
  { clave: 'bandeja', etiqueta: 'Bandeja', icono: 'bi-inbox-fill' },
  { clave: 'plantillas', etiqueta: 'Plantillas', icono: 'bi-files' },
] as const;

const CLAVES = PESTANAS.map((p) => p.clave);

export function Documentos() {
  const params = window._routeParams ?? [];
  const zona = String(params[0] ?? '');
  const idPlantilla = Number(params[1]);

  const [pestana, setPestana] = useState(
    () => ((CLAVES as readonly string[]).includes(zona) ? zona : 'bandeja'));

  if (zona === 'plantilla' && Number.isFinite(idPlantilla) && idPlantilla > 0) {
    return <Configurador id={idPlantilla} />;
  }

  return (
    <>
      <header className="mb-4">
        <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
          <i className="bi bi-file-earmark-text-fill" aria-hidden="true" /> Documentos
        </h1>
        <p className="mt-1 text-3 text-muted">
          Formatos PDF oficiales: se rellenan con los datos del colaborador y quedan radicados
          como evidencia.
        </p>
      </header>

      <Tabs
        valor={pestana}
        onCambio={(clave) => {
          const nueva = (CLAVES as readonly string[]).includes(clave) ? clave : 'bandeja';
          setPestana(nueva);
          history.replaceState(null, '', `#documentos/${nueva}`);
        }}
        pestanas={[...PESTANAS]}
      >
        <TabPanel clave="bandeja"><Bandeja activa={pestana === 'bandeja'} /></TabPanel>
        <TabPanel clave="plantillas"><Plantillas activa={pestana === 'plantillas'} /></TabPanel>
      </Tabs>
    </>
  );
}
